import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState
} from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ArrowUpDown, ExternalLink } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { ArticleRow } from "../api/client";
import { PaginationControls } from "./PaginationControls";
import { getPrimaryArticleUrl, shouldShowCanonicalLink } from "../utils/article-links";
import { DEFAULT_ARTICLE_PAGE_SIZE, clampPageIndex, paginateRows } from "../utils/pagination";
import { ArticleCategories } from "./ArticleCategories";

const numberFormat = new Intl.NumberFormat("zh-HK");

export function TopArticlesTable({ rows }: { rows: ArticleRow[] }) {
  const [sorting, setSorting] = useState<SortingState>([{ id: "views", desc: true }]);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_ARTICLE_PAGE_SIZE);
  const columns = useMemo(
    () => [
      {
        accessorKey: "title",
        header: "文章",
        cell: ({ row }: { row: { original: ArticleRow } }) => {
          const article = row.original;

          return (
            <div className="article-cell">
              <a href={getPrimaryArticleUrl(article)} target="_blank" rel="noreferrer">
                {article.title}
                <ExternalLink size={13} aria-hidden="true" />
              </a>
              <span className="article-meta-line">
                <span>
                  {article.article_id} · {article.author || "Unknown"}
                </span>
                {shouldShowCanonicalLink(article) ? (
                  <a className="canonical-link" href={article.canonical_url} target="_blank" rel="noreferrer">
                    canonical
                    <ExternalLink size={12} aria-hidden="true" />
                  </a>
                ) : null}
              </span>
            </div>
          );
        }
      },
      {
        id: "categories",
        accessorFn: (row: ArticleRow) => row.categories.join(" "),
        header: "分類",
        cell: ({ row }: { row: { original: ArticleRow } }) => (
          <ArticleCategories categories={row.original.categories} />
        )
      },
      {
        accessorKey: "is_member_only",
        header: "類型",
        cell: ({ row }: { row: { original: ArticleRow } }) => (
          <span className={row.original.is_member_only ? "badge member" : "badge public"}>
            {row.original.is_member_only ? "會員" : "一般"}
          </span>
        )
      },
      {
        accessorKey: "published_date",
        header: "發布日"
      },
      {
        accessorKey: "views",
        header: "觀看",
        cell: ({ row }: { row: { original: ArticleRow } }) => numberFormat.format(row.original.views)
      },
      {
        accessorKey: "unique_visitors",
        header: "訪客",
        cell: ({ row }: { row: { original: ArticleRow } }) => numberFormat.format(row.original.unique_visitors)
      }
    ],
    []
  );
  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableMultiSort: false,
    enableSortingRemoval: true,
    sortDescFirst: false
  });
  const sortedRows = table.getSortedRowModel().rows;
  const page = paginateRows(sortedRows, pageIndex, pageSize);

  useEffect(() => {
    setPageIndex(0);
  }, [rows, pageSize, sorting]);

  useEffect(() => {
    setPageIndex((current) => clampPageIndex(current, sortedRows.length, pageSize));
  }, [sortedRows.length, pageSize]);

  const renderPaginationControls = () => (
    <PaginationControls
      pageIndex={page.pageIndex}
      pageSize={page.pageSize}
      totalRows={sortedRows.length}
      onPageIndexChange={setPageIndex}
      onPageSizeChange={setPageSize}
    />
  );

  return (
    <div className="table-stack">
      {renderPaginationControls()}
      <div className="table-wrap">
        <table>
          <thead>
            {table.getHeaderGroups().map((group) => (
              <tr key={group.id}>
                {group.headers.map((header) => {
                  const direction = header.column.getIsSorted();
                  const ariaSort = direction === "asc" ? "ascending" : direction === "desc" ? "descending" : "none";
                  const nextDirection = header.column.getNextSortingOrder();
                  const sortTitle = nextDirection === "asc" ? "按升冪排列" : nextDirection === "desc" ? "按降冪排列" : "取消排序";

                  return (
                    <th key={header.id} aria-sort={ariaSort}>
                      <button
                        type="button"
                        title={sortTitle}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {direction === "asc" ? (
                          <ArrowUp size={13} aria-hidden="true" />
                        ) : direction === "desc" ? (
                          <ArrowDown size={13} aria-hidden="true" />
                        ) : (
                          <ArrowUpDown size={13} aria-hidden="true" />
                        )}
                      </button>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {page.items.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {renderPaginationControls()}
    </div>
  );
}
